import { useMemo, useState } from 'react';
import { KeyRound, UserPlus, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useGrantMeetingAccess, useRevokeMeetingAccess } from '../../hooks/useMeetingLog';
import { useUsers } from '../../hooks/useUsers';
import type { Meeting, MeetingAccessLevel } from '../../lib/meetingLogApi';
import { Select } from '../ui/Select';

/**
 * "Au acces": colleagues who may open this meeting without having been in it,
 * each able to view or to edit. Separate from "Colegi prezenți" so the list of
 * who was in the room stays true. People whose role already sees every meeting
 * do not need to be here, which the hint under the title says.
 */
export function MeetingAccessCard({
  meeting,
  canManage,
}: {
  meeting: Meeting;
  canManage: boolean;
}) {
  const { t } = useI18n();
  const { data: users } = useUsers();
  const grant = useGrantMeetingAccess(meeting.id);
  const revoke = useRevokeMeetingAccess(meeting.id);
  const [userId, setUserId] = useState('');
  const [level, setLevel] = useState<MeetingAccessLevel>('VIEW');

  const levelOptions = [
    { value: 'VIEW', label: t('meetings.share.view') },
    { value: 'EDIT', label: t('meetings.share.edit') },
  ];

  // Anyone already on the meeting has their access from that, not from here.
  const candidates = useMemo(() => {
    const taken = new Set<string>([
      meeting.owner.id,
      ...(meeting.createdBy ? [meeting.createdBy.id] : []),
      ...meeting.teamMembers.flatMap((m) => (m.userId ? [m.userId] : [])),
      ...meeting.access.map((a) => a.userId),
    ]);
    return (users ?? [])
      .filter((u) => !taken.has(u.id))
      .map((u) => ({ value: u.id, label: u.name }));
  }, [users, meeting]);

  const failed = grant.isError || revoke.isError;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        <KeyRound size={14} />
        {t('meetings.share.title')}
      </h2>
      <p className="mb-3 mt-1 text-xs text-gray-500">{t('meetings.share.hint')}</p>

      {meeting.access.length === 0 ? (
        <p className="text-sm text-gray-400">{t('meetings.share.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {meeting.access.map((entry) => (
            <li key={entry.userId} className="flex items-center gap-2 text-sm">
              <span className="min-w-0 flex-1 truncate text-gray-800 sm:max-w-xs sm:flex-none sm:basis-72">
                {entry.name}
              </span>
              {canManage ? (
                <>
                  <Select
                    label={t('meetings.share.levelFor', { name: entry.name })}
                    hideLabel
                    size="sm"
                    value={entry.level}
                    options={levelOptions}
                    onChange={(next) =>
                      grant.mutate({ userId: entry.userId, level: next as MeetingAccessLevel })
                    }
                    className="w-36 shrink-0"
                  />
                  <button
                    type="button"
                    onClick={() => revoke.mutate(entry.userId)}
                    disabled={revoke.isPending}
                    aria-label={t('meetings.share.remove', { name: entry.name })}
                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                  >
                    <X size={15} />
                  </button>
                </>
              ) : (
                <span className="shrink-0 text-xs text-gray-500">
                  {t(entry.level === 'EDIT' ? 'meetings.share.edit' : 'meetings.share.view')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <Select
            label={t('meetings.share.colleague')}
            hideLabel
            searchable
            value={userId}
            options={candidates}
            placeholder={t('meetings.share.pick')}
            onChange={setUserId}
            className="w-full sm:w-72"
          />
          <Select
            label={t('meetings.share.level')}
            hideLabel
            value={level}
            options={levelOptions}
            onChange={(next) => setLevel(next as MeetingAccessLevel)}
            className="w-40"
          />
          <button
            type="button"
            disabled={!userId || grant.isPending}
            onClick={() => grant.mutate({ userId, level }, { onSuccess: () => setUserId('') })}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-fixed-white hover:bg-brand-700 disabled:opacity-50"
          >
            <UserPlus size={15} /> {t('meetings.share.add')}
          </button>
        </div>
      )}
      {failed && <p className="mt-2 text-sm text-red-600">{t('meetings.access.failed')}</p>}
    </section>
  );
}
